import { useContext, useState } from "react";

import * as Sentry from "@sentry/react";

import {
  parseError,
  useAddToCartMutation,
  useUpdateOrderBundleMutation,
} from "../api/wordpress.ts";
import { shouldUseEntry, shouldUseStep } from "../helpers/steps.ts";
import { useBooking } from "../providers/BookingProvider.tsx";
import { ConfigurationContext } from "../providers/ConfigurationContext.tsx";
import { useVehicleSelection } from "../providers/VehicleSelectionContext.tsx";
import {
  AddToCartRequest,
  UpdateOrderBundleRequest,
} from "../types/Product.ts";

interface ErrorWithoutMessage {
  type: "cart-not-empty" | "invalid-nonce";
}

interface ErrorWithMessage {
  type: "generic";
  message: string;
}

interface ValidationError {
  type: "validation";
  fields: {
    stepIndex: number;
    entryIndex: number;
  }[];
}

type CartError = ErrorWithoutMessage | ErrorWithMessage | ValidationError;

const useAddToCart = () => {
  const {
    pricing,
    selectedOptions,
    selectedGvmUpgrade,
    selectedOptionDetails,
    orderMode,
    setValidationErrors,
    notes,
  } = useVehicleSelection();

  const { order } = useBooking();
  const { wcStoreNonce } = useContext(ConfigurationContext);

  const [error, setError] = useState<CartError | null>(null);

  const setGenericError = (message: string) => {
    setError({
      type: "generic",
      message,
    });
  };

  const clearError = () => {
    setError(null);
  };

  const [addToCart, { isLoading }] = useAddToCartMutation();
  const [updateOrder, { isLoading: isUpdating }] =
    useUpdateOrderBundleMutation();

  const triggerAddToCart = async () => {
    clearError();
    setValidationErrors([]);

    if (!selectedGvmUpgrade) {
      setGenericError("A GVM upgrade has not yet been selected.");
      return;
    }

    // Validate required fields
    const bundleData = selectedGvmUpgrade.extensions.cc_complex_bundle;
    const invalidFields: { stepIndex: number; entryIndex: number }[] = [];

    for (const [stepIndex, step] of bundleData.steps.entries()) {
      if (!shouldUseStep(step, orderMode, selectedOptions)) {
        continue;
      }
      for (const [entryIndex, entry] of step.entries.entries()) {
        if (!shouldUseEntry(entry, orderMode, selectedOptions)) {
          continue;
        }
        const entryKey = `${stepIndex}-${entryIndex}`;
        const selectedOption = selectedOptions[entryKey];

        if (!entry.entry_required) {
          continue;
        }

        if (selectedOption === undefined || selectedOption === null) {
          invalidFields.push({ stepIndex, entryIndex });
        }
      }
    }

    if (invalidFields.length > 0) {
      setValidationErrors(invalidFields);
      setError({
        type: "validation",
        fields: invalidFields,
      });
      return;
    }

    const selections: Record<string, { quantity: number }> = {};
    for (const [key, option] of Object.entries(selectedOptions)) {
      const [stepIndex, entryIndex] = key.split("-").map(Number);

      if (option !== null && option !== undefined) {
        if (Array.isArray(option)) {
          for (const opt of option) {
            const optionKey = `${stepIndex}-${entryIndex}-${opt}`;
            selections[optionKey] = {
              quantity: selectedOptionDetails[optionKey]?.quantity || 1,
            };
          }
        } else {
          const optionKey = `${stepIndex}-${entryIndex}-${option}`;
          selections[optionKey] = {
            quantity: selectedOptionDetails[optionKey]?.quantity || 1,
          };
        }
      }
    }

    const payload: AddToCartRequest = {
      id: selectedGvmUpgrade.id,
      nonce: wcStoreNonce,
      selections,
      pricing: {
        total: pricing.total.toFixed(2),
      },
      notes: notes || undefined,
    };

    if (orderMode === "new") {
      addToCart(payload)
        .unwrap()
        .then(() => {
          // Redirect to cart page after successful addition
          window.location.href = "/gvm-quote-checkout";
        })
        .catch((error) => {
          const parsedError = parseError(error);

          if (parsedError.code === "jmacx_cart_must_be_empty") {
            setError({
              type: "cart-not-empty",
            });
          } else if (parsedError.code === "woocommerce_rest_invalid_nonce") {
            setError({
              type: "invalid-nonce",
            });
          } else {
            Sentry.captureException(error, {
              extra: {
                action: "addToCart",
                parsedErrorMessage: parsedError.message,
                payload: JSON.stringify(payload),
              },
            });

            setGenericError(
              "There was an error adding items to your cart. " +
                parsedError.message,
            );
          }
        });
    } else {
      if (!order) {
        setGenericError("The order hasn't been loaded.");
        return;
      }

      const updatePayload: UpdateOrderBundleRequest = {
        ...payload,
        order_id: order.id,
      };

      updateOrder(updatePayload)
        .unwrap()
        .then(() => {
          alert("The GVM build has been updated.");
        })
        .catch((error) => {
          const parsedError = parseError(error);

          if (parsedError.code === "woocommerce_rest_invalid_nonce") {
            setError({
              type: "invalid-nonce",
            });
          } else {
            Sentry.captureException(error, {
              extra: {
                action: "updateOrder",
                parsedErrorMessage: parsedError.message,
                payload: JSON.stringify(updatePayload),
              },
            });

            setGenericError(
              "There was an error updating the order. " + parsedError.message,
            );
          }
        });
    }
  };

  return {
    triggerAddToCart,
    isLoading: isLoading || isUpdating,
    error,
    clearError,
  };
};

export default useAddToCart;
